import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
} from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';

import { Fonts, Palette } from '@/constants/theme';
import { webNoOutline } from '@/lib/web-style';
import { webTransition } from '@/lib/hover-style';
import { looksLikeMarkdown, markdownToHtml } from '@/components/RichTextView';

interface Props {
  html: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onSubmit?: () => void;
}

/**
 * Rich-text editor — Wave 36 trim:
 *   Toolbar reduced to the essentials the user actually reaches for:
 *   Bold, Italic, Underline, Bullet list, Numbered list. Removed:
 *   H1/H2 (headings rarely useful in personal notes), quote (likewise),
 *   text-color picker (extra UI surface for niche use), image paste
 *   (saving base64 images bloats SQLite and the user didn't want it).
 *
 * Bugs fixed this wave:
 *   - "First letter jumps to end" (typing "test" → "estT"): caused by
 *     the auto-focus useEffect placing the caret AT END of empty
 *     contents, then setEmpty(false) re-rendering the placeholder
 *     overlay mid-type which made the browser reset the caret to
 *     position 0. Fix: placeholder is now a pure CSS pseudo-element
 *     keyed off a data-empty attribute we toggle on the contentEditable
 *     itself — no React re-render, no caret reset.
 *   - Enter inserts a giant double-line gap: contentEditable's default
 *     Enter creates `<div>` wrappers that pick up the editor's
 *     line-height. We intercept Enter and insert a single `<br>` via
 *     execCommand('insertLineBreak') so a newline is one line, not two.
 */
export function RichTextEditor({ html, onChange, placeholder, onSubmit }: Props) {
  if (Platform.OS === 'web') {
    return <WebEditor html={html} onChange={onChange} placeholder={placeholder} onSubmit={onSubmit} />;
  }
  return <NativeEditor html={html} onChange={onChange} placeholder={placeholder} />;
}

// ---- Web (contentEditable) ----------------------------------------------

function WebEditor({ html, onChange, placeholder, onSubmit }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  // `lastHtmlRef` stores the LAST html we either initialized with OR
  // emitted via onChange. The prop-sync effect compares incoming props
  // against this; if they match, it's our own round-trip and we skip
  // the DOM resync. If they don't, something external changed the
  // value (e.g. note switched) and we sync.
  const lastHtmlRef = useRef<string>('');
  // Has the initial-mount DOM seed already run? Set to true exactly
  // once (right after we set innerHTML on the very first commit). Used
  // to gate the markdown-promotion path so it ONLY runs at mount —
  // never on subsequent prop changes that come from our own emit.
  const mountedRef = useRef(false);

  /**
   * Toggle the data-empty attribute on the contentEditable so the CSS
   * pseudo-element placeholder shows/hides without a React re-render.
   * (Re-rendering an overlay placeholder was triggering caret resets
   * in an earlier draft.)
   */
  const syncEmptyAttr = () => {
    const el = ref.current;
    if (!el) return;
    const isEmpty = !el.textContent?.trim() && !/<img/i.test(el.innerHTML);
    if (isEmpty) {
      el.setAttribute('data-empty', '1');
    } else {
      el.removeAttribute('data-empty');
    }
  };

  /**
   * Mount: seed the contentEditable's innerHTML ONCE, with markdown
   * promoted to HTML if the incoming body was legacy markdown. After
   * this, the prop-sync effect compares props verbatim — never
   * re-promotes markdown, never blindly resyncs.
   *
   * This was the actual root cause of the "estT" bug: looksLikeMarkdown
   * returns `true` for any string without a `<`, so a single character
   * like "t" got promoted to "<p>t</p>" on every emit round-trip; the
   * prop-diff failed ("t" !== "<p>t</p>"); the DOM resync overwrote
   * innerHTML mid-typing; the caret reset to position 0; subsequent
   * chars landed before the first. By gating promotion to mount-only,
   * the round-trip diff sees "t" === "t" and never touches the DOM.
   */
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const el = ref.current;
    if (!el || mountedRef.current) return;
    const seed = looksLikeMarkdown(html) ? markdownToHtml(html) : html;
    el.innerHTML = seed;
    lastHtmlRef.current = seed;
    syncEmptyAttr();
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Subsequent prop changes. After mount, an incoming `html` prop that
   * matches `lastHtmlRef.current` is our own round-trip — skip. If it
   * differs, an external caller switched the note or programmatically
   * updated the body; sync DOM verbatim (no markdown promotion).
   */
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!mountedRef.current) return;
    if (lastHtmlRef.current === html) return;
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html;
    lastHtmlRef.current = html;
    syncEmptyAttr();
  }, [html]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    syncEmptyAttr();
    const raw = el.innerHTML;
    const sanitized = sanitizeHtml(raw);
    // Store the sanitized form so the next prop-sync effect's diff
    // against the incoming sanitized prop matches and skips resync.
    lastHtmlRef.current = sanitized;
    onChange(sanitized);
  };

  // Auto-focus the body shortly after mount so the user can type
  // immediately when the modal opens. We DELIBERATELY do not move
  // the caret — letting the browser default to the start of content
  // avoids the prior collapse(false) pattern that placed the caret
  // at a phantom end-of-empty-content position.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const t = setTimeout(() => {
      ref.current?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, []);

  const exec = (command: string, value?: string) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).execCommand(command, false, value);
    emit();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      exec('bold');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      exec('italic');
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') {
      e.preventDefault();
      exec('underline');
      return;
    }
    // Plain Enter inside a list item: let the browser handle it so we
    // get a new <li>. Outside a list: insert a single line break
    // instead of a paragraph/div so the line spacing is one line, not
    // two. Detect "inside list" by walking up from the caret.
    if (e.key === 'Enter' && !e.shiftKey) {
      const sel = window.getSelection();
      const node = sel?.anchorNode;
      let n: Node | null = node ?? null;
      while (n && n !== ref.current) {
        const tag = (n as HTMLElement).nodeName?.toLowerCase?.();
        if (tag === 'li') return; // let browser default handle it
        n = n.parentNode;
      }
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).execCommand('insertLineBreak');
      emit();
    }
  };

  /**
   * Paste handler: text-only. Images and rich HTML formatting get
   * stripped down to plain text — keeps storage tiny and matches the
   * trimmed-toolbar philosophy (no inline images, no foreign styles).
   * The user said remove the image-upload affordance entirely; this
   * goes one step further and also drops images that arrive via paste.
   */
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dt = (e as any).clipboardData;
    if (!dt) return;
    const text = dt.getData?.('text/plain') ?? '';
    const insert = escapeHtml(text).replace(/\n/g, '<br>');
    insertHtmlAtCursor(insert);
    emit();
  };

  /**
   * Attach a mousedown handler to the toolbar wrapper that calls
   * preventDefault. This is what makes B/I/U/lists actually fire —
   * without it the contentEditable loses focus the moment the user
   * clicks a button, and execCommand has no selection to act on.
   */
  const onToolbarMount = useCallback((node: View | null) => {
    if (Platform.OS !== 'web' || !node) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dom = node as unknown as HTMLElement;
    if (!dom?.addEventListener) return;
    const handler = (ev: MouseEvent) => ev.preventDefault();
    dom.addEventListener('mousedown', handler);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dom as any).__rteMouseDownHandler = handler;
  }, []);

  return (
    <View style={styles.wrap}>
      <View ref={onToolbarMount} style={styles.toolbar}>
        <TBtn Icon={Bold} label="Bold (Ctrl+B)" onPress={() => exec('bold')} />
        <TBtn Icon={Italic} label="Italic (Ctrl+I)" onPress={() => exec('italic')} />
        <TBtn Icon={Underline} label="Underline (Ctrl+U)" onPress={() => exec('underline')} />
        <Divider />
        <TBtn Icon={List} label="Bullet list" onPress={() => exec('insertUnorderedList')} />
        <TBtn Icon={ListOrdered} label="Numbered list" onPress={() => exec('insertOrderedList')} />
      </View>
      <View style={styles.bodyWrap}>
        {(() => {
          const Div = 'div' as unknown as React.ComponentType<{
            ref: (n: HTMLDivElement | null) => void;
            contentEditable: boolean;
            suppressContentEditableWarning?: boolean;
            onInput: () => void;
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
            onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
            spellCheck?: boolean;
            style?: object;
            'data-rte'?: string;
            'data-placeholder'?: string;
          }>;
          return (
            <Div
              ref={(n: HTMLDivElement | null) => {
                // Capture the DOM node only. Initial innerHTML seeding
                // and the markdown-promotion path live in the mount
                // useEffect above — keeping that in a single place
                // (and gated by mountedRef) prevents the duplicate
                // promotion that caused the estT bug.
                ref.current = n;
              }}
              contentEditable
              suppressContentEditableWarning
              onInput={emit}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              spellCheck
              data-rte="body"
              data-placeholder={placeholder ?? ''}
              style={webEditorStyle as object}
            />
          );
        })()}
      </View>
      {/* Pseudo-element placeholder rule (injected once globally via a
          style tag) — keying off the contentEditable's data-empty
          attribute avoids a React rerender when the user starts typing,
          which was the root cause of the first-char-jumps-to-end bug. */}
      <RTEStyleInjector />
    </View>
  );
}

/**
 * Inject a single <style> tag with the placeholder + paragraph rules
 * we want inside the contentEditable. Runs on mount and removes its
 * own tag if it was the last consumer (best-effort; harmless if not).
 */
function RTEStyleInjector() {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const ID = 'homestead-rte-style';
    if (document.getElementById(ID)) return;
    const tag = document.createElement('style');
    tag.id = ID;
    tag.textContent = `
      [data-rte="body"][data-empty="1"]::before {
        content: attr(data-placeholder);
        color: ${Palette.inkFaint};
        font-family: HankenGrotesk_400Regular, sans-serif;
        font-size: 14px;
        line-height: 20px;
        pointer-events: none;
      }
      /* Tighten the default Enter behavior — paragraphs and divs the
         browser inserts get zero margin so a newline is visually one
         line, not two. We intercept Enter in JS to insert <br> instead
         of a <div>, but in case the browser slips a wrapper in (e.g.
         after a paste that contains line breaks), this keeps spacing
         tight. */
      [data-rte="body"] p,
      [data-rte="body"] div {
        margin: 0;
      }
      [data-rte="body"] ul,
      [data-rte="body"] ol {
        margin: 4px 0;
        padding-left: 20px;
      }
    `;
    document.head.appendChild(tag);
  }, []);
  return null;
}

/**
 * Insert HTML at the current selection cursor. Used by the paste
 * handler when the user pastes plain text.
 */
function insertHtmlAtCursor(html: string): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const template = document.createElement('template');
  template.innerHTML = html;
  const frag = template.content;
  const lastNode = frag.lastChild;
  range.insertNode(frag);
  if (lastNode) {
    const newRange = document.createRange();
    newRange.setStartAfter(lastNode);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
  }
}

// ---- Toolbar button -----------------------------------------------------

function TBtn({
  Icon,
  label,
  onPress,
  active,
}: {
  Icon: typeof Bold;
  label: string;
  onPress: () => void;
  active?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityLabel={label}
      hitSlop={4}
      style={({ pressed, hovered }) => [
        styles.tbtn,
        webTransition,
        hovered && styles.tbtnHover,
        active && styles.tbtnActive,
        pressed && { transform: [{ scale: 0.92 }] },
      ]}>
      <Icon size={14} color={active ? Palette.forest : Palette.ink} strokeWidth={2} />
    </Pressable>
  );
}

function Divider() {
  return <View style={styles.tDivider} />;
}

// ---- Native fallback -----------------------------------------------------

function NativeEditor({
  html,
  onChange,
  placeholder,
}: Pick<Props, 'html' | 'onChange' | 'placeholder'>) {
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const ref = useRef<TextInput | null>(null);

  const wrap = (tag: string) => {
    const { start, end } = selection;
    const before = html.slice(0, start);
    const middle = html.slice(start, end) || 'text';
    const after = html.slice(end);
    onChange(`${before}<${tag}>${middle}</${tag}>${after}`);
    setTimeout(() => ref.current?.focus(), 0);
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.toolbar}>
        <TBtn Icon={Bold} label="Bold" onPress={() => wrap('strong')} />
        <TBtn Icon={Italic} label="Italic" onPress={() => wrap('em')} />
        <TBtn Icon={Underline} label="Underline" onPress={() => wrap('u')} />
      </View>
      <TextInput
        ref={ref}
        value={html}
        onChangeText={onChange}
        onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        multiline
        textAlignVertical="top"
        style={[styles.nativeInput, webNoOutline]}
      />
    </View>
  );
}

// ---- Sanitization --------------------------------------------------------

/**
 * Strip every tag that isn't on the whitelist. Wave 36 trim — removed
 * h1/h2/h3, blockquote, span (color), and img from the whitelist
 * since the toolbar no longer produces them and the editor doesn't
 * accept them via paste either. Anything from a legacy note body
 * outside the new whitelist gets demoted to plain text on next save.
 */
function sanitizeHtml(input: string): string {
  // Step 1: strip <font> wrappers + wrapper <div>s from contentEditable.
  let s = input.replace(/<\/?font[^>]*>/gi, '');
  s = s.replace(/<\/?div[^>]*>/gi, '');
  // Step 2: rewrite the remaining whitelisted tags, drop the rest.
  const allowed = new Set([
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'code',
  ]);
  return s.replace(/<(\/?)([a-zA-Z0-9]+)([^>]*)>/g, (_full, slash, tag) => {
    const t = tag.toLowerCase();
    if (!allowed.has(t)) return '';
    return `<${slash}${t}>`;
  });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ---- Styles --------------------------------------------------------------

const webEditorStyle: { [k: string]: string | number } = {
  minHeight: 220,
  padding: 16,
  fontFamily:
    'HankenGrotesk_400Regular, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  fontSize: 14,
  lineHeight: '20px',
  color: Palette.ink,
  outline: 'none',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const styles = StyleSheet.create({
  wrap: { flex: 1, backgroundColor: Palette.card, minHeight: 280 },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 2,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    flexWrap: 'wrap',
  },
  tDivider: {
    width: 1,
    height: 18,
    backgroundColor: Palette.line,
    marginHorizontal: 4,
  },
  tbtn: {
    width: 32,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tbtnHover: { backgroundColor: Palette.sand },
  tbtnActive: { backgroundColor: '#EAF0EA' },
  bodyWrap: { flex: 1, position: 'relative', minHeight: 220 },
  nativeInput: {
    flex: 1,
    padding: 16,
    fontFamily: Fonts.sans,
    fontSize: 14,
    lineHeight: 20,
    color: Palette.ink,
    minHeight: 220,
  },
});
